import { useState } from "react";
import { toast } from "sonner";
import { Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { applyBrandVars, useBranding } from "@/context/BrandingContext";
import { PALETTES } from "@/lib/brandingOptions";

/* Compact palette switcher for the user menu — instant live application:
   CSS vars flip optimistically, then the choice persists via the branding API. */
export const PaletteMenuSection = () => {
  const { branding, refresh } = useBranding();
  const [busy, setBusy] = useState(false);

  const apply = async (p) => {
    if (busy) return;
    setBusy(true);
    applyBrandVars(p.primary, p.accent, branding?.preset); // instant, no reload
    try {
      await api.put("/tenant/branding", { primary_color: p.primary, accent_color: p.accent });
      await refresh();
      toast.success(`${p.name} palette applied`);
    } catch (e) {
      await refresh(); // roll vars back to server truth
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const active = (p) =>
    branding?.primary_color?.toLowerCase() === p.primary.toLowerCase()
    && branding?.accent_color?.toLowerCase() === p.accent.toLowerCase();

  return (
    <div className="px-2 py-2" data-testid="palette-quick-switcher">
      <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 px-1 mb-1.5">
        Theme palette
      </p>
      <div className="grid grid-cols-3 gap-1">
        {PALETTES.map((p) => (
          <button key={p.name} title={p.name} disabled={busy}
            data-testid={`palette-quick-${p.name.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            onClick={() => apply(p)}
            className="relative flex h-7 border border-slate-200 hover:border-slate-400 overflow-hidden">
            <span className="flex-1" style={{ background: p.primary }} />
            <span className="w-2.5" style={{ background: p.accent }} />
            {active(p) && (
              <span className="absolute inset-0 flex items-center justify-center">
                <Check className="h-3.5 w-3.5 text-white drop-shadow" strokeWidth={3} />
              </span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};
